import { RegistrationStatus } from "@fieldday/fielddayportal-model"
import { createContext, useContext, useEffect, useRef, useState } from "react"
import { ScheduledActivityImage } from "../models/ScheduledActivityImage"
import { WEBIP_CURRENT_ID, isNonVolunteerEvent, isVolunteerEvent } from "../util/scheduledActivityUtils"
import { useAPI } from "../util/useAPI"
import { useNonprofitContext } from "./commonOrgContext"
import { useTeamRoleContext } from "./teamRoleContext"
import { useAuth } from "./useAuth"
import { AlertSeverity, useLoading } from "./useLoading"
import { useScheduledActivityContext } from "./useScheduledActivityContext"

type ContextProps = {
  children: React.ReactNode
}

type ImagesForContext = {
  scheduledActivityImages: ScheduledActivityImage[] | null
  retrieveScheduledActivityImages: (orgUnitId?: string) => void
  imagesLoaded: boolean
  canSeePhotos: boolean
  canUploadPhotos: boolean
}

const ScheduledActivityImagesContext = createContext<ImagesForContext>(
  { scheduledActivityImages: {}} as ImagesForContext
)

export function ScheduledActivityImagesContextProvider({ children }: ContextProps) {
  const scheduledActivityImages = useProvideScheduledActivityImages()

  return (
    <ScheduledActivityImagesContext.Provider value={scheduledActivityImages}>
      {children}
    </ScheduledActivityImagesContext.Provider>
  )
}

export const useScheduledActivityImagesContext = () => {
  return useContext(ScheduledActivityImagesContext)
}

export function useProvideScheduledActivityImages(): ImagesForContext {
  const FieldDayAPI = useAPI()
  const { user } = useAuth()
  const { setAlert } = useLoading()
  const { canCoordinate: isNpoCoordinator } = useNonprofitContext()
  const { canCoordinate: isTeamCoordinator, isMember: isTeamMember, orgUnit } = useTeamRoleContext()
  const { scheduledActivity, registrations, guestRegistrations, ouIdForTeamMessagesAndPhotos } = useScheduledActivityContext()
  const scheduledActivityId = scheduledActivity.id

  const imageAbortController = useRef(new AbortController)

  const [scheduledActivityImages, setImages] = useState<ScheduledActivityImage[] | null>(null)
  const [imagesETag, setImagesETag] = useState("")

  const myReg = [...registrations, ...guestRegistrations].find(r => r.userId === user?.id)
  const userRegistered = !!myReg && myReg.status !== RegistrationStatus.Withdrawn
  const canSeePhotos = userRegistered || isNpoCoordinator() || isTeamMember()
  const isNpoCoordOrPOC = (myReg?.status === RegistrationStatus.Point_Of_Contact) || isNpoCoordinator()
  // this logic is all so painful, we're going to have to revisit it some day
  const isNonVolOrWeBIP = isNonVolunteerEvent(scheduledActivity) || scheduledActivity.id === WEBIP_CURRENT_ID
  const isVolNotWeBIP = isVolunteerEvent(scheduledActivity) && scheduledActivity.id !== WEBIP_CURRENT_ID
  const canUploadPhotos = ((userRegistered || isTeamCoordinator()) && isVolNotWeBIP) ||
    (isNonVolOrWeBIP && !!myReg?.orgUnitId) || isNpoCoordOrPOC
  const ouIdForTeamHubImages = !!orgUnit && isTeamMember() ? orgUnit.id : undefined

  const retrieveScheduledActivityImages = () => {

    if (!scheduledActivityId || !canSeePhotos) return // only get photos if user is signed in
    const ouId = ouIdForTeamHubImages ?? ouIdForTeamMessagesAndPhotos
    FieldDayAPI.listScheduledActivityImages(scheduledActivityId, imageAbortController.current, ouId)
      .then(axiosResp => {
        const newScheduledActivityImages = axiosResp.data
        const respETag = axiosResp.headers?.etag

        if (respETag) {
          if (respETag !== imagesETag) {
            setImages(newScheduledActivityImages)
          }
          setImagesETag(respETag)
        }
      }).catch(err => {
        const errMsg = err.response?.data?.message || `${err.message}`
        if (errMsg === "canceled") {
          console.warn("canceled request for images due to context switch")
        } else {
          setAlert(AlertSeverity.ERROR, errMsg)
        }
      })
  }

  useEffect(() => {
    if (!canSeePhotos) return

    retrieveScheduledActivityImages()

    return () => {
      // When the component is unmounted, cancel any
      // requests made with the imageAbortController
      imageAbortController.current.abort()
    }
  }, [canSeePhotos])

  return {
    scheduledActivityImages,
    retrieveScheduledActivityImages,
    imagesLoaded: scheduledActivityImages !== null,
    canSeePhotos,
    canUploadPhotos,
  }
}
